import { Controller } from "@hotwired/stimulus"

// Schnell-Check "Reicht die Kraft noch zum Selbstschieben?" auf /elektrorollstuhl.
// Vier Fragen (Gehstrecke, Eigenantrieb, Einsatzort, Rezept) spiegeln die
// Funnel-Fragen. Das Ergebnis ist eine Einschätzung, keine medizinische
// Beurteilung: Über die Verordnung entscheidet der Arzt, über die
// Kostenübernahme die Krankenkasse (§ 33 SGB V). Pflegegrad wird bewusst
// nicht abgefragt, er ist keine Anspruchsvoraussetzung.
//
// Leitplanke fürs Ergebnis: Der Funnel-CTA steht nur dann vorn, wenn ein
// manueller Rollstuhl aus eigener Kraft nicht (mehr) geht — sonst entstehen
// Anfragen, die die Kasse ablehnt. Ausnahme: Liegt schon eine Verordnung für
// einen Elektrorollstuhl vor (Frage 4 fragt genau das), hat der Arzt
// entschieden; dann führt jeder Zweig direkt zur Probefahrt.
export default class extends Controller {
  static values = { probefahrtUrl: String, elektromobilUrl: String }

  static targets = [
    "option", "progress", "placeholder", "result",
    "badge", "title", "text", "hints", "ctaPrimary", "ctaSecondary"
  ]

  // Antworten überleben ein Re-Connect (Stimulus ruft connect() beim Wiederanhängen erneut auf)
  initialize() {
    this.answers = { geh: null, kraft: null, ort: null, rezept: null }
  }

  connect() {
    this.render()
  }

  select(event) {
    const { frage, value } = event.currentTarget.dataset
    this.answers[frage] = value
    this.render()
  }

  render() {
    this.optionTargets.forEach(btn => {
      const on = this.answers[btn.dataset.frage] === btn.dataset.value
      btn.classList.toggle("border-ph24_new_blue", on)
      btn.classList.toggle("bg-ph24_new_blue/5", on)
      btn.classList.toggle("ring-2", on)
      btn.classList.toggle("ring-ph24_new_blue", on)
      btn.classList.toggle("border-gray-200", !on)
      btn.classList.toggle("hover:border-ph24_new_blue/40", !on)
      btn.setAttribute("aria-pressed", on ? "true" : "false")
    })

    const answered = Object.values(this.answers).filter(Boolean).length
    this.progressTarget.textContent = `${answered} von 4 Fragen beantwortet`

    const done = answered === 4
    this.placeholderTarget.classList.toggle("hidden", done)
    this.resultTarget.classList.toggle("hidden", !done)
    if (!done) return

    const verdict = this.withRezept(this.verdict())
    this.badgeTarget.textContent = verdict.badge
    this.titleTarget.textContent = verdict.title
    this.textTarget.textContent = verdict.text
    this.hintsTarget.replaceChildren(...verdict.hints.map(hint => {
      const li = document.createElement("li")
      li.className = "flex gap-2"
      const dot = document.createElement("span")
      dot.className = "text-ph24_new_blue font-bold"
      dot.textContent = "·"
      const span = document.createElement("span")
      span.textContent = hint
      li.append(dot, span)
      return li
    }))
    this.link(this.ctaPrimaryTarget, verdict.primary)
    this.link(this.ctaSecondaryTarget, verdict.secondary)
    this.resultTarget.dataset.verdict = verdict.key
  }

  // Verordnung für einen Elektrorollstuhl liegt vor: der ärztliche Teil ist erledigt,
  // also kein "So bekommen Sie eine Verordnung" mehr — Probefahrt-Funnel nach vorn.
  // Über die Kostenübernahme entscheidet trotzdem die Kasse (§ 33 SGB V).
  withRezept(verdict) {
    if (this.answers.rezept !== "ja" || verdict.key === "elektro") return verdict
    return {
      ...verdict,
      title: "Verordnung liegt vor: Der nächste Schritt ist die Probefahrt zu Hause",
      text: "Mit der Verordnung für einen Elektrorollstuhl ist der ärztliche Teil erledigt; über die Kostenübernahme entscheidet die Kasse anhand von Verordnung und Kostenvoranschlag. Jetzt zählt, dass das Modell zu Wohnung, Auto und Alltagswegen passt: Das prüft ein Vertragspartner der Kassen bei der Probefahrt zu Hause und reicht anschließend den Kostenvoranschlag ein. Bewilligt die Kasse, zahlen Sie nur die gesetzliche Zuzahlung von 5 bis 10 €.",
      hints: [this.ortHint()],
      primary: { label: "Kostenlose Probefahrt zu Hause vereinbaren", href: this.probefahrtUrlValue },
      secondary: { label: "Wie die Kostenübernahme abläuft", href: "#krankenkasse" }
    }
  }

  ortHint() {
    return {
      innen: "Für die Wohnung zählen Wendekreis und Breite: Kompakte Innenraum-Modelle kommen durch Türen und um enge Ecken.",
      beides: "Innen und außen ist der häufigste Fall. Wird der Rollstuhl im Auto mitgenommen, lohnt ein faltbares Leichtmodell; sonst bietet ein Standardmodell mehr Reichweite und Federung.",
      lang: "Für weite Wege draußen zählen Reichweite, Federung und Beleuchtung. Kassenmodelle fahren 6 km/h; seit dem BSG-Urteil vom April 2024 sind Reichweite und Geschwindigkeit allein kein Ablehnungsgrund mehr."
    }[this.answers.ort]
  }

  link(anchor, cta) {
    anchor.classList.toggle("hidden", !cta)
    if (!cta) return
    anchor.href = cta.href
    anchor.querySelector("span").textContent = cta.label
  }

  verdict() {
    const { geh, kraft, ort, rezept } = this.answers
    const kraftOk = kraft === "ja"
    const gehWeit = geh === "weit"
    const lang = ort === "lang"

    // Beide Reserven reichen: Kasse versorgt mit Rollator / manuellem Rollstuhl
    if (kraftOk && gehWeit) {
      return {
        key: "manuell",
        badge: "Einschätzung",
        title: "Ein manueller Rollstuhl oder ein Rollator reicht vermutlich aus",
        text: "Sie schaffen noch längere Strecken zu Fuß und könnten einen Rollstuhl selbst anschieben. In dieser Situation versorgt die Krankenkasse in der Regel mit dem einfacheren Hilfsmittel: einem Rollator oder einem manuellen Rollstuhl auf Rezept. Ein Elektrorollstuhl wäre medizinisch schwer zu begründen.",
        hints: lang
          ? ["Für längere Wege draußen kann ein Elektromobil (Scooter) sinnvoll sein. Die Kasse übernimmt es nur bei nachgewiesenem Bedarf, viele kaufen es privat."]
          : ["Ändert sich Ihre Belastbarkeit, lohnt sich der Check erneut: Ausschlaggebend ist, ob Sie einen Rollstuhl dauerhaft aus eigener Kraft bewegen können."],
        primary: { label: "Manueller Rollstuhl auf Rezept: so geht es", href: "#manueller-rollstuhl" },
        secondary: lang ? { label: "Elektromobil-Probefahrt anfragen", href: this.elektromobilUrlValue } : null
      }
    }

    // Anschieben klappt noch: Kasse prüft zuerst die einfachere Versorgung
    if (kraftOk) {
      return {
        key: "grenze",
        badge: "Einschätzung",
        title: "Erste Wahl der Kasse ist der manuelle Rollstuhl, der Elektroantrieb kommt, wenn die Kraft nicht mehr reicht",
        text: "Ihre Gehstrecke ist eingeschränkt, aber Arme und Schultern reichen noch zum Selbstschieben. Die Krankenkasse prüft zuerst die einfachere Versorgung, also den manuellen Rollstuhl. Lässt die Kraft nach oder werden die Alltagswege zu weit, ist der Wechsel auf einen Elektrorollstuhl medizinisch begründbar.",
        hints: [
          "Achten Sie darauf, ob Sie nach dem Anschieben erschöpft sind oder Schultern und Handgelenke schmerzen: Mit dieser Begründung verordnen Ärzte einen Elektrorollstuhl.",
          lang
            ? "Für weite Strecken draußen kommen ein Zusatzantrieb für den manuellen Rollstuhl oder ein Elektromobil in Frage. Seit dem BSG-Urteil vom April 2024 zählen die tatsächlichen Wege zu Arzt, Apotheke und Supermarkt statt einer festen Entfernung."
            : "Rund 9 von 10 Anfragen bei uns betreffen leichte, faltbare Modelle: Sie lassen sich im Auto mitnehmen und sind ein guter nächster Schritt, wenn das Anschieben schwerer wird."
        ],
        primary: { label: "So läuft die Verordnung ab", href: "#rezept" },
        secondary: { label: "Unverbindlich zu Hause beraten lassen", href: this.probefahrtUrlValue }
      }
    }

    // Gehen klappt, Anschieben nicht: erst ärztlich einordnen lassen
    if (gehWeit) {
      return {
        key: "arzt",
        badge: "Einschätzung",
        title: "Gehen klappt noch, Anschieben nicht: Das sollte Ihr Arzt einordnen",
        text: "Wer noch längere Strecken zu Fuß schafft, braucht aus Sicht der Krankenkasse meist noch keinen Rollstuhl. Gelingt das Gehen aber nur mit Schmerzen, Pausen oder Sturzangst, sagen Sie das Ihrem Arzt genau so. Für die Kasse zählt Ihre tatsächliche Belastbarkeit im Alltag mehr als die Strecke auf dem Papier.",
        hints: [
          "Ist ein Rollstuhl nötig, spricht die fehlende Kraft zum Selbstschieben für einen Elektroantrieb. Ein Pflegegrad ist dafür nicht erforderlich.",
          "Lassen Sie sich Diagnose und Einschränkung auf der Verordnung konkret begründen: Das ist die Grundlage, auf der die Kasse entscheidet."
        ],
        primary: { label: "So bekommen Sie eine Verordnung", href: "#rezept" },
        secondary: { label: "Unverbindlich zu Hause beraten lassen", href: this.probefahrtUrlValue }
      }
    }

    // Kernfall: Gehstrecke eingeschränkt und kein Eigenantrieb möglich
    const rezeptHint = {
      ja: "Mit der Verordnung kann ein Vertragspartner der Kassen direkt den Kostenvoranschlag einreichen. Die Probefahrt zu Hause klärt vorher, welches Modell zu Wohnung, Auto und Wegen passt.",
      beantragt: "Bis zum Arzttermin ist die Probefahrt zu Hause der sinnvolle nächste Schritt: Dann stehen das passende Modell und die Begründung für die Verordnung fest.",
      nein: "Rund 7 von 10 Anfragen bei uns starten ohne Rezept. Der erste Schritt ist der Arzttermin; unsere Partner unterstützen bei Verordnung und Antrag."
    }[rezept]
    const ortHint = this.ortHint()

    return {
      key: "elektro",
      badge: "Einschätzung",
      title: "Ein Elektrorollstuhl ist in Ihrer Situation medizinisch gut begründbar",
      text: `Sie schaffen zu Fuß nur noch ${geh === "kurz" ? "wenige Schritte" : "kurze Strecken"} und können einen Rollstuhl nicht dauerhaft aus eigener Kraft bewegen. Genau das ist die Voraussetzung, unter der die Krankenkasse einen Elektrorollstuhl als Hilfsmittel übernehmen kann (§ 33 SGB V); die Entscheidung trifft die Kasse anhand von Verordnung und Kostenvoranschlag. Bewilligt sie, zahlen Sie nur die gesetzliche Zuzahlung von 5 bis 10 €. Ein Pflegegrad ist dafür nicht nötig.`,
      hints: [rezeptHint, ortHint],
      primary: { label: "Kostenlose Probefahrt zu Hause vereinbaren", href: this.probefahrtUrlValue },
      secondary: { label: "Wie die Kostenübernahme abläuft", href: "#krankenkasse" }
    }
  }
}
